import React from 'react';

import find from 'lodash/find';

import NoResultsMessage from 'app/components/general/NoResultsMessage';
import {useCurrentUserState} from 'app/current-user/api';
import {getCurrentUserPerms} from 'app/current-user/utils';
import {sweetAPI} from 'app/store/sweet-api';
import {runWhenAllRelatedMatch} from 'app/subscriptions';
import {
  useBulkAddDocumentsToTeam,
  useDetachFromTeam,
  useTeams,
} from 'app/teams/api';
import {convertLocationParamsToFetchParams} from 'app/teams/utils';
import {TEAMS_CREATE_PAGE_URL} from 'app/urls';
import {getContentType} from 'app/utils/content-type';
import {gt} from 'app/utils/i18n';
import {useAsyncActions} from 'app/utils/redux';
import {TEAMTAG} from 'app/utils/rtk-tags';

import {RTKGenericAttachedItemsSidebar} from './GenericAttachedItemsSidebar/RTKGenericAttachedItemsSidebar';

import type {ModelChangeMessageType} from 'app/subscriptions';
import type {
  DetailPolicySerializedType,
  ProcedureHashidSerializedType,
  ProcessSerializedType,
  QuizSerializedType,
  TeamSerializedType,
} from 'sweet-types';

type Props = {
  object:
    | ProcedureHashidSerializedType
    | DetailPolicySerializedType
    | ProcessSerializedType
    | QuizSerializedType;
  attachedItems: Array<{url: string}>;
  fetchObject: () => Promise<any>;
};

export const DetailPageGenericTeamsSidebar = ({
  object,
  fetchObject,
  attachedItems,
}: Props) => {
  const {isManager, isSuperManager} = getCurrentUserPerms(
    useCurrentUserState().data!
  );
  const actions = useAsyncActions({
    invalidateTags: sweetAPI.util.invalidateTags,
  });

  const [addToTeam] = useBulkAddDocumentsToTeam();
  const [detachFromTeam] = useDetachFromTeam();

  return (
    <RTKGenericAttachedItemsSidebar
      useDataHook={useTeams}
      closeUrl={object.html_url}
      attachedItems={attachedItems}
      paramsConverter={convertLocationParamsToFetchParams}
      itemNameProcessor={(team: TeamSerializedType) => team.name}
      title={gt('Teams')}
      handleModelChange={(message: ModelChangeMessageType) => {
        runWhenAllRelatedMatch(
          message,
          'team',
          {
            content_type: object.content_type,
            id: object.id,
          },
          () => {
            actions.invalidateTags([
              // @ts-expect-error we inject tags on the API after it's created so TS doesn't know any tag type
              {type: TEAMTAG, id: 'list'},
            ]);
            fetchObject();
          }
        );
      }}
      onRemove={(team: TeamSerializedType) => {
        const teamLink = find(object.team_memberships, {
          team: team.url,
        });

        if (teamLink != null) {
          return detachFromTeam(teamLink.url)
            .unwrap()
            .then(() => fetchObject());
        }

        return fetchObject();
      }}
      showFilterField={object.can_edit}
      searchPlaceholder={gt('Search teams...')}
      onAttach={(team) => {
        return addToTeam({team: team.url, documents: [object.url]})
          .unwrap()
          .then(() => fetchObject());
      }}
      canCreate={isSuperManager}
      createLabel={gt('Create team')}
      createURL={TEAMS_CREATE_PAGE_URL}
      emptyResultsComponents={
        <NoResultsMessage
          onSidebar={true}
          icon="fa-users"
          head={gt('No Teams')}
        >
          {isManager && object.can_edit
            ? gt(
                `You can add this ${
                  getContentType(object.content_type).name
                } to existing teams, remove it from a team or create a new team using the button below`
              )
            : null}
        </NoResultsMessage>
      }
      itemComponentProps={(team: TeamSerializedType, attached) => {
        const membership = find(object.team_memberships, {
          team: team.url,
        });
        return {
          disableAndShowTooltipText:
            attached && membership?.via_process
              ? gt(
                  'The document was added to this team via a process %s',
                  membership.via_name
                )
              : attached && membership?.via_folder
              ? gt(
                  'The document was added to this team via a folder %s',
                  membership.via_name
                )
              : !object.can_edit
              ? gt('You must be a manager of this document')
              : !team.can_edit
              ? gt('Only a manager can add and remove from this team')
              : undefined,
          actionTooltip: attached
            ? object.content_type != 'policy'
              ? gt(
                  'Remove document from this team (this will also cancel any tasks related to the team.)'
                )
              : gt('Remove document from this team.')
            : gt('Add document to this team'),
        };
      }}
    />
  );
};
